import type { CSSProperties } from "react";
import type { ThemeTokens, Tweaks } from "./types";
import { resolveTheme } from "./resolveTheme";
import {
  expandTweaksFromStylePreset,
  FALLBACK_TEMPLATE_TWEAKS,
} from "./templateSettingsMap";
import { isHexDark } from "../../../Utils/HelperFunction/HelperFunction";

/**
 * Inline-edit empty-field placeholder color.
 * Dark canvases need a near-white shade — fading `currentColor` to 42% reads as
 * muddy gray on Midnight / black backgrounds. Light canvases keep the soft ink fade.
 */
export function tplEditPlaceholderColor(bg: string): string {
  return isHexDark(bg)
    ? "color-mix(in srgb, currentColor 82%, transparent)"
    : "color-mix(in srgb, currentColor 42%, transparent)";
}

/** CSS variables for bundling loaders (dashboard + public) — uses saved preset/palette/fonts when provided. */
export function tplThemeVarsForWebsiteLoader(
  tweaks?: Partial<Tweaks> | null,
  device: "desktop" | "tablet" | "mobile" = "desktop",
): CSSProperties {
  const merged = expandTweaksFromStylePreset({
    ...FALLBACK_TEMPLATE_TWEAKS,
    ...(tweaks ?? {}),
    axes: {
      ...FALLBACK_TEMPLATE_TWEAKS.axes,
      ...(tweaks?.axes ?? {}),
    },
    pages: tweaks?.pages ?? FALLBACK_TEMPLATE_TWEAKS.pages,
    contentEdits: tweaks?.contentEdits ?? FALLBACK_TEMPLATE_TWEAKS.contentEdits,
  });
  return tplThemeVars(resolveTheme(merged, device));
}

/** Maps resolved theme tokens to CSS custom properties for `template.css`. */
export function tplThemeVars(T: ThemeTokens): CSSProperties {
  return {
    "--tpl-bg": T.bg,
    "--tpl-surface": T.surface,
    "--tpl-ink": T.ink,
    "--tpl-header-ink": T.headerInk,
    "--tpl-muted": T.muted,
    /* Stable page ink/muted — accent shells remap --tpl-ink for band copy, but
       surface cards (Links grid/cards, Highlight cards) must keep page text. */
    "--tpl-page-ink": T.ink,
    "--tpl-page-muted": T.muted,
    "--tpl-primary": T.primary,
    "--tpl-secondary": T.secondary,
    "--tpl-tertiary": T.tertiary,
    "--tpl-accent": T.accent,
    "--tpl-accent2": T.accent2,
    "--tpl-accent-ink": T.accentInk,
    "--primary-color": T.bg,
    "--secondary-color": T.ink,
    "--tertiary-color": T.muted,
    "--forth-color": T.surface,
    "--tpl-display": T.display,
    "--tpl-body": T.body,
    "--tpl-ui-font": T.uiFont,
    "--tpl-font-head": T.display,
    "--tpl-font-stack": T.uiFont,
    "--tpl-mono": T.mono,
    "--tpl-display-track": T.displayTrack,
    "--tpl-display-weight": String(T.displayWeight),
    "--tpl-display-size-scale": String(T.displaySizeScale),
    "--tpl-nav-weight": String(T.navWeight),
    "--tpl-category-weight": String(T.categoryWeight),
    "--tpl-caption-weight": String(T.captionWeight),
    "--tpl-type-weight": String(T.typeLabelWeight),
    "--tpl-section-pad": T.sectionPad,
    "--tpl-stack": T.stack,
    /* Nav bar + first block below header (all pages). */
    "--tpl-nav-pad-block": "clamp(12px, 1.8cqi, 18px)",
    /* Vertical rhythm: one gap between nav → first section, section → section, last section → footer.
       Sized so the editor “+ Add component” strip fits cleanly between blocks. */
    "--tpl-section-gap": "clamp(36px, 4.5cqi, 52px)",
    "--tpl-page-top-pad": "clamp(72px, 9cqi, 104px)",
    "--tpl-page-bottom-pad": "clamp(64px, 8.5cqi, 96px)",
    /* Section heading → content loop (within a section, not between sections). */
    "--tpl-head-loop-gap": "clamp(16px, 2.5cqi, 28px)",
    "--tpl-gutter": T.gutter,
    "--tpl-radius": T.radius,
    "--tpl-radius-full": T.radiusFull,
    /* Media tiles (shop/content/search cards + search bar) — shared across pages. */
    "--tpl-media-radius": "16px",
    "--tpl-border": T.border,
    "--tpl-img-filter": T.imgFilter,
    /* Empty inline-edit placeholders (`Add text`) — contrast-aware vs canvas. */
    "--tpl-edit-placeholder": tplEditPlaceholderColor(T.bg),
  } as CSSProperties;
}
